import { useRef } from "react";
import { motion } from "framer-motion";
import { Reveal, SectionHeading } from "./Reveal";

const SHOTS = [
  {
    src: "/images/wind-up.jpg",
    alt: "Pitcher mid-delivery off the mound",
    caption: "The Wind-Up",
    span: "md:col-span-7",
    height: "h-[320px] md:h-[480px]",
  },
  {
    src: "/images/full-swing.jpg",
    alt: "Batter mid-swing at home plate",
    caption: "Full swing",
    span: "md:col-span-5",
    height: "h-[320px] md:h-[480px]",
  },
  {
    src: "/images/huddle-up.jpg",
    alt: "Team huddle on the field",
    caption: "Huddle Up",
    span: "md:col-span-4",
    height: "h-[280px] md:h-[360px]",
  },
  {
    src: "/images/safe-at-second.jpg",
    alt: "Player diving back to the base as the umpire watches",
    caption: "Safe at second",
    span: "md:col-span-4",
    height: "h-[280px] md:h-[360px]",
  },
  {
    src: "/images/team-celebration.jpg",
    alt: "Team celebrating together after a big play",
    caption: "Team celebration",
    span: "md:col-span-4",
    height: "h-[280px] md:h-[360px]",
  },
  {
    src: "/images/the-field.jpg",
    alt: "On-deck circle logo with home plate beyond",
    caption: "The field",
    span: "md:col-span-5",
    height: "h-[300px] md:h-[400px]",
  },
  {
    src: "/images/on-deck.jpg",
    alt: "Hitter waiting on deck, bat over the shoulder",
    caption: "On deck",
    span: "md:col-span-7",
    height: "h-[300px] md:h-[400px]",
  },
];

function Frame({ shot, index }) {
  const ref = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <motion.figure
      ref={ref}
      onMouseMove={onMove}
      initial={{ opacity: 0, y: 48, skewX: -4 }}
      whileInView={{ opacity: 1, y: 0, skewX: -2 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay: (index % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative ${shot.span} ${shot.height} w-full overflow-hidden border border-white/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] transition-[border-color,box-shadow] duration-500 hover:border-accentlight/50 hover:shadow-[0_30px_90px_-25px_rgba(30,136,229,0.4)]`}
      data-testid={`portfolio-frame-${index + 1}`}
    >
      <img
        src={shot.src}
        alt={shot.alt}
        loading="lazy"
        style={shot.imgPos ? { objectPosition: shot.imgPos } : undefined}
        className={`h-full w-full skew-x-[2deg] object-cover transition-transform duration-700 ease-out ${shot.zoom ?? "scale-[1.16] group-hover:scale-[1.22]"}`}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(480px circle at var(--mx, 50%) var(--my, 50%), rgba(79,195,247,0.25), transparent 65%)",
        }}
      />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-ink/90 to-transparent" />
      <figcaption className="pointer-events-none absolute bottom-4 left-5 flex items-center gap-3 md:bottom-6 md:left-7">
        <span className="inline-block h-px w-6 bg-accentlight" />
        <span className="font-display text-xl tracking-widest text-white md:text-2xl">
          {shot.caption}
        </span>
      </figcaption>
    </motion.figure>
  );
}

export default function Gallery() {
  return (
    <section id="work" className="relative mx-auto w-full max-w-7xl px-6 py-24 md:px-10 md:py-36">
      <div className="mb-14 flex flex-col gap-6 md:mb-20 md:flex-row md:items-end md:justify-between">
        <SectionHeading
          eyebrow="Selected Frames"
          title={
            <>
              MOMENTS THAT
              <br />
              <span className="bg-gradient-to-r from-accent to-accentlight bg-clip-text text-transparent">
                VANISH IN SECONDS
              </span>
            </>
          }
        />
        <Reveal delay={0.15}>
          <p className="max-w-sm text-sm leading-relaxed text-white/60">
            Swings, slides and slow-motion heartbreak — shot from the fence line,
            edited the same week. This is the good stuff from recent games.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
        {SHOTS.map((shot, i) => (
          <Frame key={shot.src} shot={shot} index={i} />
        ))}
      </div>
    </section>
  );
}
